/**
 * Grafik digambar sebagai SVG langsung, tanpa pustaka.
 *
 * Bentuk yang dibutuhkan hanya tiga dan semuanya sederhana; menambah pustaka
 * grafik berarti ~150 KB JavaScript demi tiga bentuk yang tiap-tiapnya di
 * bawah tiga puluh baris. Konsekuensinya tidak ada animasi dan tooltip bawaan
 * — keduanya memang tidak dibutuhkan di laporan yang dicetak.
 */

/**
 * Tabel data pendamping setiap grafik.
 *
 * SVG di sini murni gambar: pembaca layar tidak bisa menyusuri titik-titiknya,
 * dan pengguna yang mencetak hitam-putih kehilangan pembeda warnanya. Tabelnya
 * dilipat supaya tidak mengganggu yang membaca grafik secara visual, tapi
 * tetap ada di DOM dan ikut tercetak saat dibuka.
 *
 * `BarList` tidak memakainya: angka setiap batang sudah tertulis sebagai teks.
 */
function ChartTable({
  caption,
  columns,
  rows,
}: {
  caption: string;
  columns: [string, string];
  rows: [string, string][];
}) {
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-[13px] font-medium text-ink-secondary">
        Lihat sebagai tabel
      </summary>
      <table className="mt-2 w-full text-[13px]">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-border text-left text-ink-secondary">
            <th className="py-1.5 font-medium">{columns[0]}</th>
            <th className="py-1.5 text-right font-medium">{columns[1]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-b border-border last:border-0">
              <td className="py-1.5 text-ink">{label}</td>
              <td className="tabular py-1.5 text-right text-ink">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/** Batang horizontal — untuk rata-rata per wilayah dan peringkat TBM. */
export function BarList({
  items,
  max = 100,
  formatValue = (v: number) => String(Math.round(v * 10) / 10),
}: {
  items: { label: string; value: number; hint?: string }[];
  max?: number;
  formatValue?: (v: number) => string;
}) {
  if (items.length === 0) {
    return <p className="text-[13px] text-ink-muted">Belum ada data.</p>;
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {items.map((item) => (
        <li key={item.label}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[14px] text-ink">{item.label}</span>
            <span className="tabular shrink-0 text-[14px] font-semibold text-ink">
              {formatValue(item.value)}
              {item.hint && <span className="ml-1.5 font-normal text-ink-muted">{item.hint}</span>}
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-app"
            role="img"
            aria-label={`${item.label}: ${formatValue(item.value)}`}
          >
            <div
              className="h-full rounded-full bg-primary"
              style={{
                width: `${Math.max(0, Math.min(100, (item.value / max) * 100))}%`,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Sebaran kategori sebagai batang bertumpuk satu baris. */
export function BandBar({
  bands,
  colors,
}: {
  bands: { name: string; count: number }[];
  colors?: Record<string, string>;
}) {
  const total = bands.reduce((a, b) => a + b.count, 0);
  if (total === 0) return <p className="text-[13px] text-ink-muted">Belum ada penilaian.</p>;

  const palette = [
    'var(--color-success)',
    'var(--color-primary)',
    '#f5a623',
    '#e58a2b',
    'var(--color-ink-muted)',
  ];

  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-app">
        {bands.map((b, i) => (
          <div
            key={b.name}
            style={{
              width: `${(b.count / total) * 100}%`,
              background: colors?.[b.name] ?? palette[i % palette.length],
            }}
            title={`${b.name}: ${b.count}`}
          />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {bands.map((b, i) => (
          <li key={b.name} className="flex items-center gap-1.5 text-[13px]">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{
                background: colors?.[b.name] ?? palette[i % palette.length],
              }}
              aria-hidden
            />
            <span className="text-ink">{b.name}</span>
            <span className="tabular font-semibold text-ink-secondary">{b.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Radar per dimensi. Sumbu memakai PERSENTASE pencapaian dimensi (nilai ÷
 * bobot), bukan nilai mentahnya — dimensi berbobot 25 dan 10 tidak bisa
 * dibandingkan langsung, dan radar yang membandingkannya akan selalu terlihat
 * timpang meski capaiannya sama baiknya.
 */
export function RadarChart({
  items,
  size = 260,
}: {
  items: { label: string; value: number; max: number }[];
  size?: number;
}) {
  if (items.length < 3) {
    return <p className="text-[13px] text-ink-muted">Radar butuh minimal tiga dimensi.</p>;
  }

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 34;
  const n = items.length;

  const point = (i: number, ratio: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + Math.cos(angle) * r * ratio, cy + Math.sin(angle) * r * ratio] as const;
  };

  const polygon = items
    .map((it, i) => point(i, it.max > 0 ? Math.min(1, it.value / it.max) : 0).join(','))
    .join(' ');

  const grid = [0.25, 0.5, 0.75, 1];

  return (
    <>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="mx-auto h-auto w-full max-w-[300px]"
        role="img"
        aria-label={`Radar capaian ${items.length} dimensi. Rinciannya tersedia pada tabel di bawah.`}
      >
        {grid.map((g) => (
          <polygon
            key={g}
            points={items.map((_, i) => point(i, g).join(',')).join(' ')}
            fill="none"
            stroke="var(--color-border)"
          />
        ))}
        {items.map((_, i) => {
          const [x, y] = point(i, 1);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--color-border)" />;
        })}

        <polygon
          points={polygon}
          fill="var(--color-primary)"
          fillOpacity="0.18"
          stroke="var(--color-primary)"
          strokeWidth="2"
        />

        {items.map((it, i) => {
          const [x, y] = point(i, 1.16);
          return (
            <text
              key={it.label}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-[color:var(--color-ink-secondary)] text-[10px]"
            >
              {it.label}
            </text>
          );
        })}
      </svg>

      <ChartTable
        caption="Capaian tiap dimensi terhadap bobotnya"
        columns={['Dimensi', 'Capaian']}
        rows={items.map((it) => [
          it.label,
          it.max > 0 ? `${Math.round((it.value / it.max) * 100)}%` : '—',
        ])}
      />
    </>
  );
}

/** Tren indeks lintas periode. */
export function TrendChart({
  points,
  height = 200,
}: {
  points: { label: string; value: number }[];
  height?: number;
}) {
  if (points.length === 0) {
    return <p className="text-[13px] text-ink-muted">Belum ada riwayat penilaian.</p>;
  }

  const w = 560;
  const padX = 36;
  const padY = 18;
  const innerW = w - padX * 2;
  const innerH = height - padY * 2;

  const x = (i: number) =>
    padX + (points.length === 1 ? innerW / 2 : (innerW * i) / (points.length - 1));
  const y = (v: number) => padY + innerH - (Math.max(0, Math.min(100, v)) / 100) * innerH;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`).join(' ');

  return (
    <div>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${w} ${height}`}
          className="h-auto w-full min-w-[420px]"
          role="img"
          aria-label={`Tren indeks ${points.length} periode. Rinciannya tersedia pada tabel di bawah.`}
        >
          {[0, 25, 50, 75, 100].map((v) => (
            <g key={v}>
              <line x1={padX} y1={y(v)} x2={w - padX} y2={y(v)} stroke="var(--color-border)" />
              <text
                x={padX - 6}
                y={y(v)}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-[color:var(--color-ink-muted)] text-[9px]"
              >
                {v}
              </text>
            </g>
          ))}

            <path d={line} fill="none" stroke="var(--color-primary)" strokeWidth="2.5" />

          {points.map((p, i) => (
            <g key={p.label}>
              <circle cx={x(i)} cy={y(p.value)} r="4" fill="var(--color-primary)" />
              <text
                x={x(i)}
                y={height - 2}
                // Label pertama dan terakhir dijangkarkan ke tepi, bukan ke
                // tengah titiknya: titik paling kiri berada di x=36, sehingga
                // label yang lebih lebar dari 72px akan terpotong keluar viewBox.
                textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                className="fill-[color:var(--color-ink-secondary)] text-[9px]"
              >
                {p.label}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <ChartTable
        caption="Indeks tiap periode"
        columns={['Periode', 'Indeks']}
        rows={points.map((p) => [p.label, String(Math.round(p.value * 10) / 10)])}
      />
    </div>
  );
}

export interface DimensionTrendItem {
  periodName: string;
  semester: number;
  index: number | null;
  dimensions?: {
    code: string;
    name: string;
    weight: number;
    value: number;
    percentage: number;
  }[];
}

const SEMESTER_PALETTE = [
  { stroke: '#0284c7', fill: '#38bdf8' },
  { stroke: '#6366f1', fill: '#818cf8' },
  { stroke: '#8b5cf6', fill: '#a78bfa' },
  { stroke: '#d97706', fill: '#fbbf24' },
  { stroke: '#e11d48', fill: '#fb7185' },
  { stroke: '#059669', fill: '#34d399' },
];

const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max) + '…' : s);

/**
 * Grafik tren capaian per dimensi antar semester.
 * Sumbu X: Dimensi instrumen (D1, D2, ...)
 * Sumbu Y: Capaian skor / persentase (0-100%)
 * Batang (bar) dan Garis (line) per semester.
 */
export function DimensionTrendChart({
  trend,
  height = 360,
}: {
  trend: DimensionTrendItem[];
  height?: number;
}) {
  const scoredSemesters = trend.filter(
    (t) => t.dimensions && t.dimensions.length > 0 && t.index !== null,
  );

  if (scoredSemesters.length === 0) {
    return <p className="text-[13px] text-ink-muted">Belum ada riwayat penilaian antar semester.</p>;
  }

  // Ambil daftar dimensi dari data penilaian
  const sampleDimensions = scoredSemesters[0]!.dimensions!;
  const dims = sampleDimensions.map((d) => ({
    code: d.code,
    name: d.name,
    weight: d.weight,
  }));

  const w = 780;
  const padLeft = 48;
  const padRight = 24;
  const padTop = 32;
  const padBottom = 68;
  const innerW = w - padLeft - padRight;
  const innerH = height - padTop - padBottom;

  const yTicks = [0, 25, 50, 75, 100];
  const getY = (pct: number) =>
    padTop + innerH - (Math.max(0, Math.min(100, pct)) / 100) * innerH;

  const slotW = innerW / dims.length;
  const numSem = scoredSemesters.length;
  const maxGroupW = slotW * 0.72;
  const barGap = 3;
  const barW = Math.max(8, Math.min(28, (maxGroupW - (numSem - 1) * barGap) / numSem));
  const groupW = numSem * barW + (numSem - 1) * barGap;

  const semesterSeries = scoredSemesters.map((sem, sIdx) => {
    const color = SEMESTER_PALETTE[sIdx % SEMESTER_PALETTE.length]!;
    const points = dims.map((dim, dIdx) => {
      const slotCenterX = padLeft + (dIdx + 0.5) * slotW;
      const groupStartX = slotCenterX - groupW / 2;
      const barX = groupStartX + sIdx * (barW + barGap);
      const centerX = barX + barW / 2;

      const dData = sem.dimensions?.find((d) => d.code === dim.code);
      const pct = dData?.percentage ?? 0;
      const val = dData?.value ?? 0;
      const y = getY(pct);

      return {
        dim,
        pct,
        val,
        weight: dim.weight,
        barX,
        barY: y,
        barH: padTop + innerH - y,
        centerX,
        centerY: y,
      };
    });

    const pathData = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.centerX} ${p.centerY}`)
      .join(' ');

    return {
      sem,
      color,
      points,
      pathData,
    };
  });

  return (
    <div className="flex flex-col gap-3">
      {/* Legenda Semester */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {semesterSeries.map(({ sem, color }) => (
          <div key={sem.periodName} className="flex items-center gap-2 text-xs">
            <span
              className="inline-block size-3 rounded-xs"
              style={{ backgroundColor: color.stroke }}
              aria-hidden
            />
            <span
              className="inline-block w-3 h-0.5 rounded-full"
              style={{ backgroundColor: color.stroke }}
              aria-hidden
            />
            <span className="font-medium text-ink">{sem.periodName}</span>
            <span className="tabular font-semibold text-ink-muted">
              ({Math.round((sem.index ?? 0) * 10) / 10})
            </span>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border bg-app/20 p-2">
        <svg
          viewBox={`0 0 ${w} ${height}`}
          className="h-auto w-full min-w-[560px]"
          role="img"
          aria-label="Grafik perbandingan capaian antar semester per dimensi"
        >
          {/* Garis Grid Horizontal & Sumbu Y */}
          {yTicks.map((tick) => {
            const y = getY(tick);
            return (
              <g key={tick}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={w - padRight}
                  y2={y}
                  stroke="var(--color-border)"
                  strokeDasharray={tick === 0 ? undefined : '3 3'}
                  strokeWidth={tick === 0 ? '1.5' : '1'}
                />
                <text
                  x={padLeft - 8}
                  y={y + 3.5}
                  textAnchor="end"
                  className="tabular text-[10px] fill-[color:var(--color-ink-muted)] font-mono"
                >
                  {tick}%
                </text>
              </g>
            );
          })}

          {/* Batang Bar per Semester di setiap Dimensi */}
          {semesterSeries.map(({ sem, color, points }) => (
            <g key={`bars-${sem.periodName}`}>
              {points.map((pt) => (
                <g key={pt.dim.code}>
                  <rect
                    x={pt.barX}
                    y={pt.barY}
                    width={barW}
                    height={Math.max(2, pt.barH)}
                    rx={3}
                    fill={color.stroke}
                    fillOpacity={0.65}
                    className="transition-all hover:fill-opacity-95"
                  >
                    <title>{`${sem.periodName} · ${pt.dim.code} ${pt.dim.name}: ${Math.round(pt.val * 10) / 10}/${pt.weight} (${Math.round(pt.pct)}%)`}</title>
                  </rect>
                  {pt.pct > 0 && (
                    <text
                      x={pt.centerX}
                      y={pt.barY - 5}
                      textAnchor="middle"
                      className="tabular text-[9px] font-semibold fill-[color:var(--color-ink-secondary)] pointer-events-none"
                    >
                      {Math.round(pt.pct)}%
                    </text>
                  )}
                </g>
              ))}
            </g>
          ))}

          {/* Garis Line & Marker Titik per Semester melintasi Dimensi */}
          {semesterSeries.map(({ sem, color, points, pathData }) => (
            <g key={`line-${sem.periodName}`}>
              <path
                d={pathData}
                fill="none"
                stroke={color.stroke}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {points.map((pt) => (
                <circle
                  key={`dot-${sem.periodName}-${pt.dim.code}`}
                  cx={pt.centerX}
                  cy={pt.centerY}
                  r={4}
                  fill="#ffffff"
                  stroke={color.stroke}
                  strokeWidth={2.5}
                >
                  <title>{`${sem.periodName} · ${pt.dim.code}: ${Math.round(pt.pct)}%`}</title>
                </circle>
              ))}
            </g>
          ))}

          {/* Sumbu X: Label Dimensi */}
          {dims.map((dim, i) => {
            const slotCenterX = padLeft + (i + 0.5) * slotW;
            return (
              <g key={dim.code}>
                <text
                  x={slotCenterX}
                  y={padTop + innerH + 18}
                  textAnchor="middle"
                  className="text-[12px] font-bold fill-[color:var(--color-ink)]"
                >
                  {dim.code}
                </text>
                <text
                  x={slotCenterX}
                  y={padTop + innerH + 32}
                  textAnchor="middle"
                  className="text-[9.5px] fill-[color:var(--color-ink-secondary)]"
                >
                  {truncate(dim.name, 16)}
                </text>
                <text
                  x={slotCenterX}
                  y={padTop + innerH + 46}
                  textAnchor="middle"
                  className="tabular text-[8.5px] fill-[color:var(--color-ink-muted)]"
                >
                  Bobot: {dim.weight}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Tabel Detail Komparasi Antar Semester */}
      <details className="mt-2">
        <summary className="cursor-pointer text-[13px] font-medium text-ink-secondary hover:text-ink">
          Lihat tabel perbandingan capaian per dimensi
        </summary>
        <div className="mt-2 overflow-x-auto rounded-[var(--radius-input)] border border-border">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-border bg-app/60 text-ink-secondary">
                <th className="px-3 py-2 font-medium">Dimensi</th>
                <th className="px-3 py-2 font-medium text-center">Bobot</th>
                {scoredSemesters.map((s) => (
                  <th key={s.periodName} className="px-3 py-2 font-medium text-center">
                    {s.periodName}
                  </th>
                ))}
                {scoredSemesters.length >= 2 && (
                  <th className="px-3 py-2 font-medium text-right">Perubahan (Delta)</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {dims.map((dim) => {
                const first = scoredSemesters[0]?.dimensions?.find((d) => d.code === dim.code);
                const last = scoredSemesters[scoredSemesters.length - 1]?.dimensions?.find(
                  (d) => d.code === dim.code,
                );
                const deltaPct =
                  scoredSemesters.length >= 2 && first && last
                    ? Math.round(last.percentage - first.percentage)
                    : null;

                return (
                  <tr key={dim.code} className="hover:bg-app/40">
                    <td className="px-3 py-2 font-medium text-ink">
                      <span className="font-bold">{dim.code}</span> · {dim.name}
                    </td>
                    <td className="px-3 py-2 text-center tabular text-ink-muted">{dim.weight}</td>
                    {scoredSemesters.map((s) => {
                      const d = s.dimensions?.find((x) => x.code === dim.code);
                      const pct = d ? Math.round(d.percentage) : 0;
                      const val = d ? Math.round(d.value * 10) / 10 : 0;
                      return (
                        <td key={s.periodName} className="px-3 py-2 text-center tabular text-ink">
                          {val} <span className="text-ink-muted">({pct}%)</span>
                        </td>
                      );
                    })}
                    {scoredSemesters.length >= 2 && (
                      <td className="px-3 py-2 text-right tabular font-medium">
                        {deltaPct !== null ? (
                          <span
                            className={
                              deltaPct > 0
                                ? 'text-success'
                                : deltaPct < 0
                                  ? 'text-danger'
                                  : 'text-ink-muted'
                            }
                          >
                            {deltaPct > 0 ? `+${deltaPct}% ↗` : deltaPct < 0 ? `${deltaPct}% ↘` : '0% —'}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
